// # 투표 캐시 — 줄에 매달린 투표
//
// 반응처럼 보이는 줄들을 **한 번에** 센다(`POST …/polls {seqs}`). 사건(`poll_changed`)은
// 수를 싣지 않는다 — `mine` 이 사람마다 달라서다. 사건이 오면 그 줄만 다시 센다.
// 내가 던진 표는 서버 답(갱신된 투표)을 그대로 적는다.

import type { ChannelId, Event, Seq } from '@maru/protocol'
import type { MaruApi, Poll } from './api.ts'

/** seq → 투표. 투표가 없는 줄은 `null` 로 기억해 다시 묻지 않는다. */
export type PollMap = ReadonlyMap<Seq, Poll | null>

const EMPTY: PollMap = new Map()

interface Held {
  by_seq: Map<Seq, Poll | null>
  wanted: Set<Seq>
  asking: Promise<void> | null
  view: PollMap
}

export class Polls {
  #api: MaruApi
  #held = new Map<ChannelId, Held>()
  #listeners = new Set<() => void>()

  constructor(api: MaruApi) {
    this.#api = api
  }

  subscribe = (listen: () => void): (() => void) => {
    this.#listeners.add(listen)
    return () => void this.#listeners.delete(listen)
  }

  /** 같은 상태면 같은 객체. */
  slice = (channel: ChannelId | null): PollMap => {
    if (channel === null) return EMPTY
    return this.#held.get(channel)?.view ?? EMPTY
  }

  /** 이 줄들이 보인다. 모르는 것만 묶어서 한 번 센다. */
  need(channel: ChannelId, seqs: readonly Seq[]): void {
    const h = this.#hold(channel)
    let added = false
    for (const s of seqs) {
      if (h.by_seq.has(s) || h.wanted.has(s)) continue
      h.wanted.add(s)
      added = true
    }
    if (added) this.#ask(channel, h)
  }

  /** 끊긴 사이 표가 움직였을 수 있다. 투표가 있던 줄만 다시 센다. */
  refresh(channel: ChannelId): void {
    const h = this.#held.get(channel)
    if (h === undefined) return
    let any = false
    for (const [s, p] of h.by_seq) {
      if (p === null) continue
      h.wanted.add(s)
      any = true
    }
    if (any) this.#ask(channel, h)
  }

  on_event(e: Event): void {
    if (e.t !== 'poll_changed') return
    const h = this.#held.get(e.channel)
    // 본 적 없는 줄이면 보일 때 센다. 방금 달린 투표는 그 줄이 `null` 로 적혀 있을 수 있다.
    if (h === undefined || !h.by_seq.has(e.seq)) return
    h.wanted.add(e.seq)
    this.#ask(e.channel, h)
  }

  /** 서버가 돌려준 투표를 그대로 적는다(만들기·던지기·거두기·닫기의 답). */
  put(p: Poll): void {
    const h = this.#hold(p.channel)
    h.by_seq.set(p.seq, p)
    this.#publish(h)
  }

  async vote(channel: ChannelId, seq: Seq, choices: readonly number[]): Promise<void> {
    this.put(await this.#api.poll_vote(channel, seq, choices))
  }

  async unvote(channel: ChannelId, seq: Seq): Promise<void> {
    this.put(await this.#api.poll_unvote(channel, seq))
  }

  async close(channel: ChannelId, seq: Seq): Promise<void> {
    this.put(await this.#api.poll_close(channel, seq))
  }

  #hold(channel: ChannelId): Held {
    let h = this.#held.get(channel)
    if (h === undefined) {
      h = { by_seq: new Map(), wanted: new Set(), asking: null, view: EMPTY }
      this.#held.set(channel, h)
    }
    return h
  }

  #ask(channel: ChannelId, h: Held): void {
    if (h.asking !== null) return
    h.asking = Promise.resolve().then(async () => {
      try {
        while (h.wanted.size > 0) {
          const seqs = [...h.wanted]
          h.wanted.clear()
          const got = await this.#api.poll_count(channel, seqs)
          const found = new Map(got.map((p) => [p.seq, p]))
          for (const s of seqs) h.by_seq.set(s, found.get(s) ?? null)
          this.#publish(h)
        }
      } catch {
        // 다음 `need` 가 다시 묻는다. 투표 때문에 화면에 오류를 띄우지 않는다.
      } finally {
        h.asking = null
      }
    })
  }

  #publish(h: Held): void {
    h.view = new Map(h.by_seq)
    for (const l of this.#listeners) l()
  }
}
